import { i18n } from '../services/i18n.js';
import { dbSim } from '../services/dbSim.js';
import { realtimeSim } from '../services/realtimeSim.js';
import { escapeHTML } from '../services/sanitize.js';
import { showReaction } from '../services/reactions.js';
import { icon } from '../services/icons.js';

export const PresentView = async (container, params) => {
  container.innerHTML = `<div class="container" style="padding:40px; text-align:center; color:var(--color-text-secondary);">${i18n.t('preparing_present')}</div>`;

  const session = await dbSim.getSession(params.id);
  if (!session) {
    window.showToast(i18n.t('session_not_found'));
    window.location.hash = '#/dashboard';
    return;
  }
  if (!['active', 'results_revealed'].includes(session.status)) {
    window.showToast(session.status === 'finished' ? i18n.t('game_finished') : i18n.t('session_not_started'));
    window.location.hash = session.status === 'finished' ? `#/reports/${session.id}` : '#/dashboard';
    return;
  }

  const game = await dbSim.getSessionGame(session.id);
  const qIdx = session.currentQuestionIndex;
  const question = game.questions[qIdx];
  
  let participants = await dbSim.getParticipants(session.id);
  const allResponses = await dbSim.getResponses(session.id);
  let responses = allResponses.filter(r => r.questionId === question.id);

  let timerVal = question.timeLimit;
  let timerInterval = null;
  let subState = session.status === 'results_revealed' ? "showing_results" : "showing_question";

  const render = () => {
    if (subState === "showing_question") {
      renderActiveQuestion();
    } else {
      renderResults();
    }
  };

  const renderActiveQuestion = () => {
    container.innerHTML = `
      <div class="host-screen" style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px; justify-content:space-between; min-height:100vh; position:relative; overflow:hidden;">
        
        <!-- Top Header -->
        <div class="host-header" style="display:flex; justify-content:space-between; align-items:center; z-index:10;">
          <span class="badge badge-red" style="font-size:14px; padding:6px 14px;">
            ${i18n.t('question')} ${qIdx + 1}/${game.questions.length}
          </span>
          <div class="session-brand session-brand-compact">
            ${game.brand?.name ? `<strong>${escapeHTML(game.brand.name)}</strong>` : ''}
            <span>${escapeHTML(game.title)}</span>
          </div>
          <span style="font-weight:700; font-size:14px; color:var(--color-text-secondary);">PIN: ${session.pin}</span>
        </div>

        <!-- Center: Question and Timer -->
        <div style="text-align:center; margin: 40px 0; z-index:10;">
          <h1 id="questionTextDisplay" style="font-size:38px; font-weight:800; line-height:1.2; margin-bottom:40px; max-width:900px; margin-left:auto; margin-right:auto;">
            ${escapeHTML(question.questionText)}
          </h1>

          <div class="present-metrics" style="display:flex; justify-content:center; align-items:center; gap:80px; margin-top:20px;">
            <!-- Countdown Circle -->
            <div id="presentTimerCircle" class="present-timer-circle" style="width:160px; height:160px; border-radius:50%; border:8px solid var(--color-primary); display:flex; flex-direction:column; align-items:center; justify-content:center; box-shadow: var(--shadow-glow); background:var(--color-surface); transition: all 0.3s ease;">
              <span id="presentTimerVal" style="font-size:56px; font-weight:800; line-height:1;">${timerVal}</span>
              <span style="font-size:11px; font-weight:600; text-transform:uppercase; color:var(--color-text-secondary); margin-top:4px;">${i18n.t('seconds')}</span>
            </div>
            
            <!-- Progress counter -->
            <div style="text-align:center;">
              <div id="answersCountText" style="font-size:64px; font-weight:800; line-height:1; color:var(--color-accent);">${responses.length}</div>
              <div style="font-size:14px; font-weight:700; color:var(--color-text-secondary); margin-top:8px;">
                ${i18n.t('answered')} / <span style="color:var(--color-text-primary); font-size:16px;">${participants.length}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Answers Grid -->
        <div class="host-answer-grid" style="display:grid; grid-template-columns: 1fr 1fr; gap:16px; z-index:10;">
          ${question.options.map((opt, idx) => {
            const colors = ['#ff3b5c', '#0070f3', '#f5a623', '#00df89'];
            const theme = colors[idx] || '#fff';
            return `
              <div class="card" style="display:flex; align-items:center; gap:20px; padding:24px; border-left: 6px solid ${theme}; background:var(--color-surface);">
                <span class="answer-label" style="color:${theme};">${String.fromCharCode(65 + idx)}</span>
                <span style="font-size:18px; font-weight:600; color:var(--color-text-primary);">${escapeHTML(opt.text)}</span>
              </div>
            `;
          }).join('')}
        </div>

      </div>
    `;

    // Start Timer
    timerInterval = setInterval(async () => {
      timerVal--;
      const timerValEl = container.querySelector('#presentTimerVal');
      const timerCircleEl = container.querySelector('#presentTimerCircle');
      
      if (timerValEl) timerValEl.innerText = timerVal;

      // Shake animation and warning colors for last 5 seconds
      if (timerVal <= 5 && timerCircleEl) {
        timerCircleEl.style.borderColor = 'var(--color-danger)';
        timerCircleEl.style.boxShadow = '0 0 30px rgba(239, 68, 68, 0.4)';
        timerCircleEl.classList.add('shake-animation');
        setTimeout(() => timerCircleEl.classList.remove('shake-animation'), 500);
      }

      if (timerVal <= 0) {
        clearInterval(timerInterval);
        await revealAnswerResults();
      }
    }, 1000);
  };

  const renderResults = () => {
    // Count option votes
    const counts = {};
    question.options.forEach(opt => counts[opt.id] = 0);
    responses.forEach(r => {
      if (counts[r.selectedOption] !== undefined) counts[r.selectedOption]++;
    });

    const maxCount = Math.max(...Object.values(counts), 1);

    container.innerHTML = `
      <div class="host-screen" style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px; justify-content:space-between; min-height:100vh;">
        
        <!-- Top Header -->
        <div class="host-header" style="display:flex; justify-content:space-between; align-items:center;">
          <span class="badge badge-green" style="font-size:14px; padding:6px 14px;">
            ${i18n.t('answer_revealed')}
          </span>
          <div class="session-brand session-brand-compact">
            ${game.brand?.name ? `<strong>${escapeHTML(game.brand.name)}</strong>` : ''}
            <span>${escapeHTML(game.title)}</span>
          </div>
          <button id="nextLeaderboardBtn" class="btn btn-accent btn-lg" style="box-shadow: var(--shadow-glow-pink); min-width:160px;">
            ${i18n.t('present_next')} ${icon('arrowRight')}
          </button>
        </div>

        <!-- Center: Question & Result Chart -->
        <div style="text-align:center; margin: 40px 0;">
          <h1 style="font-size:28px; font-weight:800; line-height:1.2; margin-bottom:40px; max-width:800px; margin-left:auto; margin-right:auto;">
            ${escapeHTML(question.questionText)}
          </h1>

          <!-- Chart Columns -->
          <div style="display:flex; justify-content:center; align-items:flex-end; gap:40px; height:240px; max-width:600px; margin:0 auto;">
            ${question.options.map((opt, idx) => {
              const colors = ['#ff3b5c', '#0070f3', '#f5a623', '#00df89'];
              const theme = colors[idx] || '#fff';
              const isCorrect = question.correctOptions.includes(opt.id);
              const voteCount = counts[opt.id];
              const pctHeight = Math.round((voteCount / maxCount) * 160) + 40; // min height 40px

              return `
                <div style="display:flex; flex-direction:column; align-items:center; flex:1; position:relative;">
                  <span style="font-weight:800; font-size:18px; margin-bottom:8px; color:var(--color-text-primary);">${voteCount}</span>
                  <div style="width:100%; height:${pctHeight}px; background:${theme}; border-radius:var(--radius-sm); transition: height 0.6s var(--ease-f1); position:relative;">
                    ${isCorrect ? `
                      <!-- Checkmark overlay for correct answer -->
                      <div style="position:absolute; top:-20px; left:50%; transform:translateX(-50%); width:32px; height:32px; border-radius:50%; background:var(--color-success); display:flex; align-items:center; justify-content:center; border:2px solid var(--color-bg); box-shadow:var(--shadow-glow-success)">${icon('check')}</div>
                    ` : ''}
                  </div>
                  <span style="font-weight:800; font-size:16px; margin-top:12px; color:${theme};">${opt.id}</span>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Explanation card (if exists) -->
        ${question.explanation ? `
          <div class="card" style="margin: 24px auto; max-width:800px; width:100%; border-color:var(--color-primary); background:rgba(255, 59, 92, 0.04); display:flex; flex-direction:column; gap:8px; padding:18px; text-align:left;">
            <div style="font-weight:800; font-size:12px; color:var(--color-primary); text-transform:uppercase; letter-spacing:1px; display:flex; align-items:center; gap:8px;">
              ${icon('lightBulb')} ${i18n.t('host_explanation')}
            </div>
            <p style="font-size:15px; line-height:1.5; color:var(--color-text-primary); font-weight:500;">
              ${escapeHTML(question.explanation)}
            </p>
          </div>
        ` : ''}

        <!-- Answers details -->
        <div class="host-answer-grid" style="display:grid; grid-template-columns: 1fr 1fr; gap:16px;">
          ${question.options.map((opt, idx) => {
            const colors = ['#ff3b5c', '#0070f3', '#f5a623', '#00df89'];
            const theme = colors[idx] || '#fff';
            const isCorrect = question.correctOptions.includes(opt.id);
            return `
              <div class="card" style="display:flex; align-items:center; gap:20px; padding:18px; border-left: 6px solid ${theme}; background:${isCorrect ? 'rgba(4, 120, 87, 0.06)' : 'var(--color-surface)'}; border-color:${isCorrect ? 'var(--color-success)' : theme}">
                <span style="font-weight:800; color:${theme}; font-size:16px;">[${opt.id}]</span>
                <span style="font-size:15px; font-weight:600; color:${isCorrect ? 'var(--color-success)' : 'var(--color-text-primary)'}; flex:1;">${escapeHTML(opt.text)}</span>
                ${isCorrect ? `<span style="color:var(--color-success);">${icon('check')}</span>` : ''}
              </div>
            `;
          }).join('')}
        </div>

      </div>
    `;

    // Bind next screen trigger
    container.querySelector('#nextLeaderboardBtn').addEventListener('click', () => {
      window.location.hash = `#/leaderboard?session=${session.id}`;
    });
  };

  const revealAnswerResults = async () => {
    clearInterval(timerInterval);
    subState = "showing_results";
    
    // Save state results_revealed
    session.status = "results_revealed";
    await dbSim.saveSession(session);

    // Compute updated leaderboard list to send points awarded
    const correctIds = question.correctOptions;
    
    // Publish show results message
    realtimeSim.publish("SHOW_RESULTS", {
      sessionId: session.id,
      questionId: question.id,
      correctOptions: correctIds
    });

    render();
  };

  // Connect & listen to players answers and reactions
  realtimeSim.connect(session.pin, async (type, payload) => {
    if (type === "SUBMIT_ANSWER" && subState === "showing_question") {
      // Re-fetch responses count
      const allResp = await dbSim.getResponses(session.id);
      responses = allResp.filter(r => r.questionId === question.id);
      
      const answersValEl = container.querySelector('#answersCountText');
      if (answersValEl) answersValEl.innerText = responses.length;

      // If everyone answered, immediately reveal results
      if (responses.length >= participants.length && participants.length > 0) {
        await revealAnswerResults();
      }
    } else if (type === "SEND_REACTION") {
      showReaction(container, payload.reaction);
    }
  });

  render();

  // Cleanup listeners
  const originalHashChange = window.onhashchange;
  window.addEventListener('hashchange', function cleanup() {
    clearInterval(timerInterval);
    realtimeSim.disconnect();
    window.removeEventListener('hashchange', cleanup);
  });
};
